import React, { useEffect, useState } from 'react';
import { useTranslate } from 'react-admin';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip as ChartTooltip, ReferenceLine } from 'recharts';
import { alpha, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Collapse from '@mui/material/Collapse';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import SearchIcon from '@mui/icons-material/Search';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { fetchFocusHistory } from '../../services/api';

// One line per alert, in the reader's language. The figures come from the server.
const alertText = (translate, alert) => {
    switch (alert.code) {
        case 'overcommitted':
            return translate('Overcommitted: %{pct} % of the capacity planned', { pct: alert.planned_pct });
        case 'idle_priority':
            return translate('"%{title}": no time logged on its projects in 4 weeks', { title: alert.title });
        case 'perception_above_measure':
            return translate('You see a heavy load, the timesheet measures %{pct} %', { pct: alert.measured_pct });
        case 'perception_below_measure':
            return translate('You see a light load, the timesheet measures %{pct} %', { pct: alert.measured_pct });
        case 'incomplete_timesheet':
            return translate('Timesheet %{pct} % complete: %{missing} days missing, last line on %{last}', {
                pct: alert.coverage_pct, missing: alert.missing_days, last: alert.last_entry_date || '-',
            });
        case 'low_timesheet_quality':
            return translate('Timesheet quality %{score}/100: read the figures with care', { score: alert.score });
        default:
            return alert.code;
    }
};

export const AlertsBanner = ({ alerts }) => {
    const translate = useTranslate();
    const theme = useTheme();
    if (!alerts?.length) {
        return null;
    }
    return (
        <Box sx={{ marginTop: '12px', padding: '8px 10px', borderRadius: '8px', backgroundColor: alpha(theme.palette.warning.main, 0.1) }}>
            {alerts.map((alert, index) => (
                <Box key={index} display="flex" gap={0.75} alignItems="flex-start" sx={{ '& + &': { marginTop: '4px' } }}>
                    <WarningAmberIcon sx={{ fontSize: 16, marginTop: '2px', color: 'warning.main' }} />
                    <Typography variant="caption">{alertText(translate, alert)}</Typography>
                </Box>
            ))}
        </Box>
    );
};

// Measured load over the last 90 days, from the daily snapshots.
export const TrendChart = ({ userId }) => {
    const translate = useTranslate();
    const theme = useTheme();
    const [points, setPoints] = useState(null);

    useEffect(() => {
        let active = true;
        fetchFocusHistory(userId, 90).then(data => active && setPoints(data.results || [])).catch(() => active && setPoints([]));
        return () => {
            active = false;
        };
    }, [userId]);

    if (!points) {
        return null;
    }
    return (
        <Box sx={{ marginTop: '14px' }}>
            <Typography variant="subtitle2">{translate('Measured load, 90 days')}</Typography>
            {points.length < 2 ? (
                <Typography variant="caption" color="text.secondary">{translate('The curve appears after a few daily computations.')}</Typography>
            ) : (
                <Box sx={{ width: '100%', height: 90 }}>
                    <ResponsiveContainer>
                        <LineChart data={points} margin={{ top: 6, right: 6, bottom: 0, left: -28 }}>
                            <XAxis dataKey="date" hide />
                            <YAxis tick={{ fontSize: 10 }} domain={[0, (max) => Math.max(120, max)]} />
                            <ReferenceLine y={100} stroke={theme.palette.text.disabled} strokeDasharray="3 3" />
                            <ChartTooltip formatter={(value) => `${value} %`} />
                            <Line type="monotone" dataKey="load_logged_pct" name={translate('Measured load')} stroke={theme.palette.primary.main} dot={false} strokeWidth={2} />
                        </LineChart>
                    </ResponsiveContainer>
                </Box>
            )}
        </Box>
    );
};

export const ClosedItems = ({ items, renderItem }) => {
    const translate = useTranslate();
    const [open, setOpen] = useState(false);
    if (!items?.length) {
        return null;
    }
    return (
        <Box sx={{ marginTop: '10px' }}>
            <Box display="flex" alignItems="center" gap={0.5} sx={{ cursor: 'pointer', color: 'text.secondary' }} onClick={() => setOpen(!open)}>
                {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                <Typography variant="caption">{translate('Paused and done (%{count})', { count: items.length })}</Typography>
            </Box>
            <Collapse in={open} unmountOnExit>
                <Box sx={{ opacity: 0.75 }}>{items.map(renderItem)}</Box>
            </Collapse>
        </Box>
    );
};

const SummaryTile = ({ value, label, detail, color }) => (
    <Box sx={{ padding: '10px 12px', borderRadius: '10px', border: '1px solid', borderColor: 'divider', minWidth: 0 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, lineHeight: 1.1, color }}>{value}</Typography>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        {detail && <Typography variant="caption" color="text.secondary" component="p" noWrap title={detail}>{detail}</Typography>}
    </Box>
);

export const TeamSummary = ({ summary }) => {
    const translate = useTranslate();
    const theme = useTheme();
    if (!summary) {
        return null;
    }
    const plannedPct = summary.capacity_days_week ? Math.round(100 * summary.planned_days_week / summary.capacity_days_week) : 0;
    return (
        <Card variant="outlined" sx={{ borderRadius: '12px', marginBottom: '16px' }}>
            <CardContent sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))', lg: 'repeat(6, minmax(0, 1fr))' }, gap: 1.5, paddingBottom: '16px !important' }}>
                <SummaryTile
                    value={`${summary.planned_days_week.toLocaleString()} / ${summary.capacity_days_week.toLocaleString()} ${translate('d/wk')}`}
                    label={translate('Planned on the team capacity')}
                    detail={`${plannedPct} %`}
                />
                <SummaryTile value={summary.over.length} label={translate('Above 110 % measured')} detail={summary.over.join(', ')}
                    color={summary.over.length ? theme.palette.error.main : undefined} />
                <SummaryTile value={summary.under.length} label={translate('Under 70 % measured')} detail={summary.under.join(', ')}
                    color={summary.under.length ? theme.palette.warning.main : undefined} />
                <SummaryTile value={summary.active_items} label={translate('Active priorities')}
                    detail={summary.without_priority.length ? `${translate('Without priority')} : ${summary.without_priority.join(', ')}` : ''} />
                <SummaryTile value={(summary.incomplete || []).length} label={translate('Timesheets to complete')}
                    detail={(summary.incomplete || []).join(', ')}
                    color={(summary.incomplete || []).length ? theme.palette.error.main : undefined} />
                <SummaryTile value={summary.alerts} label={translate('Alerts to look at')}
                    color={summary.alerts ? theme.palette.warning.main : undefined} />
            </CardContent>
        </Card>
    );
};

export const SORTS = ['name', 'measured', 'planned'];

export const BoardFilters = ({ search, onSearch, sort, onSort, withoutPriority, onWithoutPriority }) => {
    const translate = useTranslate();
    return (
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap" sx={{ marginBottom: '16px', '& .MuiFormControl-root': { minWidth: '0 !important' } }}>
            <TextField
                size="small"
                placeholder={translate('Who is on… (priority, project, headline)')}
                value={search}
                onChange={(event) => onSearch(event.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                sx={{ width: 340, maxWidth: '100%' }}
            />
            <TextField select size="small" label={translate('Sort by')} value={sort} onChange={(event) => onSort(event.target.value)} sx={{ width: 200 }}>
                <MenuItem value="name">{translate('Name')}</MenuItem>
                <MenuItem value="measured">{translate('Measured load')}</MenuItem>
                <MenuItem value="planned">{translate('Planned load')}</MenuItem>
            </TextField>
            <FormControlLabel
                control={<Switch size="small" checked={withoutPriority} onChange={(event) => onWithoutPriority(event.target.checked)} />}
                label={<Typography variant="body2">{translate('Only people without priority')}</Typography>}
            />
        </Box>
    );
};

// Does a card answer "who is on X": priorities, linked projects, headline, where the time went.
export const matchesSearch = (card, search) => {
    const needle = search.trim().toLowerCase();
    if (!needle) {
        return true;
    }
    const haystack = [
        card.user.fullname,
        card.headline,
        ...card.items.flatMap(item => [item.title, item.description, ...(item.project_names || [])]),
        ...(card.top_projects || []).map(project => project.name),
    ].filter(Boolean).join(' ').toLowerCase();
    return haystack.includes(needle);
};

export const sortCards = (cards, sort) => {
    const byName = (a, b) => a.user.fullname.localeCompare(b.user.fullname);
    if (sort === 'measured') {
        return [...cards].sort((a, b) => (b.load_logged_pct ?? -1) - (a.load_logged_pct ?? -1) || byName(a, b));
    }
    if (sort === 'planned') {
        return [...cards].sort((a, b) => (b.load_planned_pct ?? -1) - (a.load_planned_pct ?? -1) || byName(a, b));
    }
    return [...cards].sort(byName);
};
